import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useBrandContext } from '../../context/BrandContext';

import Button from '../../components/button/Button';
import ButtonSecondary from '../../components/buttonSecondary/ButtonSecondary';
import Input from '../../components/input/Input';
import ToastForgotPassword from './toastForgotPassword/ToastForgotPassword';

import { forgotPassword } from '../../services/forgotPasswordService';

import email from '../../assets/email.svg';
import logout from '../../assets/logout.svg';
import { validateRegexEmail } from '../../utils/format';

import './ForgotPassword.css';
import { useHeaderContext } from '../../context/HeaderContext';

const ForgotPassword = () => {
  type Inputs = { email: string };
  const [form, setForm] = useState<Inputs>({ email: '' });
  type Touched = Partial<Record<keyof Inputs, boolean>>;
  const [touched, setTouched] = useState<Touched>({});
  type Errors = Partial<Record<keyof Inputs, string>>;
  const [errorsValidate, setErrorsValidate] = useState<Errors>({});
  const [errorFetch, setErrorFetch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const [resendDisabled, setResendDisabled] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(30);

  const { setHeaderConfig } = useHeaderContext();

  useEffect(() => {
    const savedTime = localStorage.getItem('forgotPasswordTimer');
    if (savedTime) {
      const elapsed = Math.floor((Date.now() - parseInt(savedTime)) / 1000);
      const remaining = 30 - elapsed;
      if (remaining > 0) {
        setResendDisabled(true);
        setSecondsLeft(remaining);
      } else {
        localStorage.removeItem('forgotPasswordTimer');
      }
    }
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout | number;

    if (resendDisabled && secondsLeft > 0) {
      timer = setTimeout(() => {
        setSecondsLeft((prev: number) => prev - 1);
      }, 1000);
    }

    if (secondsLeft <= 0) {
      setResendDisabled(false);
      setSecondsLeft(30);
      localStorage.removeItem('forgotPasswordTimer');
    }

    return () => clearTimeout(timer);
  }, [resendDisabled, secondsLeft]);

  const brandContext = useBrandContext();

  const navigate = useNavigate();

  const validate = (newInputs: Inputs): Errors => {
    let newErrors: Errors = {};
    if (!newInputs.email.trim()) {
      newErrors.email = 'Campo Obrigatório';
    } else if (!validateRegexEmail(newInputs.email)) {
      newErrors.email = 'Por favor insira um e-mail válido';
    }

    setErrorsValidate(newErrors);
    return newErrors;
  };

  const handleKeyPress = (event: any) => {
    if (event.key === ' ') {
      event.preventDefault();
    }
  };

  const backToLogin = () => {
    navigate(`/${brandContext.brandName}/login`);
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const errors = validate(form);
    if (Object.keys(errors).length === 0) {
      handleForgotPassword();
    }
  };

  const handleForgotPassword = async () => {
    setIsLoading(true);
    try {
      const response = await forgotPassword(form.email);
      if (response.status === 200) {
        setModalOpen(true);
        setErrorFetch('');
        setResendDisabled(true);
        setSecondsLeft(30);
        localStorage.setItem('forgotPasswordTimer', Date.now().toString());
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      if (error?.response?.status === 404) {
        setErrorFetch('E-mail não cadastrado.');
      } else if (error?.message === 'Network Error') {
        setErrorFetch(
          'Sua conexão está instável. Por favor, tente mais tarde.'
        );
      } else {
        setErrorFetch('Falha no servidor. Por favor tente novamente.');
      }
    }
  };

  useEffect(() => {
    setHeaderConfig({
      headerText: 'Esqueci a senha',
      showLogo: true,
      showActionButtons: false,
    });
    return () => setHeaderConfig({});
  }, [navigate, setHeaderConfig]);

  return (
    <>
      <div className='forgot-password-container'>
        <div className='forgot-password-subtitle'>
          <p>
            Para alterar a senha,
            <br />
            informe seu e-mail
          </p>
        </div>
        <form onSubmit={handleSubmit} className='forgot-password-form'>
          <div className='forgot-password-fields'>
            <Input
              labelName='E-mail'
              htmlFor='email'
              type='text'
              id='email'
              className={
                errorsValidate.email
                  ? 'input-pattern error-input'
                  : 'input-pattern'
              }
              disabled={modalOpen}
              placeholder='Insira seu e-mail'
              value={form.email}
              onKeyDown={(event) => handleKeyPress(event)}
              onChange={(event) => {
                setForm({ ...form, email: event.target.value });
              }}
              onBlur={() => setTouched({ ...touched, email: true })}
              isErrorValidate={errorsValidate.email ? true : false}
              messageError={errorsValidate.email}
            />
            <b className='error-fetch-forgot-password'>
              {' '}
              {errorFetch ? errorFetch : null}
            </b>
          </div>
          <Button
            style={{
              backgroundColor: '#fff',
              color: '#787E7D',
              border: '1px solid #787E7D',
            }}
            type='submit'
            children={resendDisabled ? `Aguarde ${secondsLeft}s...` : 'Enviar'}
            image={<img alt='logout-icon' src={email} />}
            loading={isLoading}
            disabled={isLoading || modalOpen || resendDisabled}
            spinnerSecondary={true}
          />
          <ButtonSecondary
            children={`Voltar ao login`}
            image={<img alt='logout-icon' src={logout} />}
            disabled={isLoading || modalOpen}
            onClick={backToLogin}
          />
        </form>
      </div>
      <ToastForgotPassword
        isLoading={isLoading}
        email={form.email}
        allowButtonClosed={true}
        handleForgotPassword={handleForgotPassword}
        isModalOpen={modalOpen}
        setIsModalOpen={setModalOpen}
        resendDisabled={resendDisabled}
        setResendDisabled={setResendDisabled}
        secondsLeft={secondsLeft}
        setSecondsLeft={setSecondsLeft}
      />
    </>
  );
};

export default ForgotPassword;
